// Settings → MCP: connect an AI agent (Claude, ChatGPT, Gemini, Perplexity or
// a developer tool) to this account, see what's connected, and disconnect it.
// Most agents connect by signing in (OAuth); developer tools can use a
// personal token instead. The backend is routes/agents.py.
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bot, Check, Copy, KeyRound, Loader2, Unplug } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAppStore } from '../../store/appStore';
import { formatRegionalDate } from '../../lib/regionConfig';
import { AGENT_GUIDES, agentsApi } from '../../lib/agents';

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Couldn’t copy. Select the text and copy it instead.');
    }
  };
  return (
    <Button type="button" variant="outline" onClick={copy} className="shrink-0">
      {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
      {copied ? 'Copied' : label}
    </Button>
  );
}

export default function McpTab() {
  const region = useAppStore((s) => s.settings?.region);
  const [data, setData] = useState(null);
  const [guide, setGuide] = useState('claude');
  const [tokenName, setTokenName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);

  const load = useCallback(() => {
    agentsApi.list().then(setData).catch(() => toast.error('Couldn’t load your agents.'));
  }, []);
  useEffect(load, [load]);

  const date = (iso) => (iso ? formatRegionalDate(iso, region) : null);
  const active = AGENT_GUIDES.find((g) => g.id === guide);

  const disconnect = async (connection) => {
    if (!window.confirm(`Disconnect ${connection.name}? It stops working in your account straight away.`)) return;
    try {
      await agentsApi.disconnect(connection.id);
      toast.success(`${connection.name} disconnected`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Couldn’t disconnect it.');
    }
  };

  const createToken = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const made = await agentsApi.createToken(tokenName.trim());
      setNewToken(made);
      setTokenName('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Couldn’t make a token.');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm(`Revoke “${token.name}”? Anything using it stops working straight away.`)) return;
    try {
      await agentsApi.revokeToken(token.id);
      if (newToken?.row?.id === token.id) setNewToken(null);
      toast.success('Token revoked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Couldn’t revoke it.');
    }
  };

  if (!data) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="mcp-tab">
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Bot className="w-[18px] h-[18px] text-clay" /> Connect an AI agent
          </CardTitle>
          <CardDescription>
            Let Claude, ChatGPT or another AI agent work in your account: look things up, draft estimates and add your
            gear. It drafts; you check and send.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="mcp-url">Your MCP address</Label>
            <div className="flex gap-2">
              <Input id="mcp-url" readOnly value={data.mcp_url} onFocus={(e) => e.target.select()} className="font-mono text-sm" />
              <CopyButton text={data.mcp_url} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Agent">
              {AGENT_GUIDES.map((g) => (
                <Button
                  key={g.id}
                  type="button"
                  role="tab"
                  aria-selected={guide === g.id}
                  variant={guide === g.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setGuide(g.id)}
                  data-testid={`mcp-guide-${g.id}`}
                >
                  {g.name}
                </Button>
              ))}
            </div>
            <ol className="space-y-2 text-sm list-decimal pl-5">
              {active.steps.map((step) => (
                <li key={step}>
                  {step.replace('<address>', data.mcp_url).split('`').map((part, i) => (
                    i % 2 ? <code key={part} className="rounded bg-mist px-1.5 py-0.5 font-mono text-xs break-all">{part}</code> : part
                  ))}
                </li>
              ))}
            </ol>
            {active.note && <p className="text-sm text-muted-foreground">{active.note}</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Unplug className="w-[18px] h-[18px] text-clay" /> Connected agents
          </CardTitle>
          <CardDescription>Agents you signed in and approved. Disconnect one and it stops straight away.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.connections.length === 0 ? (
            <p className="text-sm text-muted-foreground">None yet. Follow the steps above and it will show here.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.connections.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Connected {date(c.created_at)}
                      {c.last_used_at ? ` · last used ${date(c.last_used_at)}` : ''}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => disconnect(c)}>Disconnect</Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <KeyRound className="w-[18px] h-[18px] text-clay" /> Personal tokens
          </CardTitle>
          <CardDescription>
            For developer tools that take a key instead of signing in. Each works for 90 days and only for your agent.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {newToken && (
            <div className="space-y-3 rounded-md bg-ochre-soft p-3" data-testid="mcp-new-token">
              <p className="text-sm">Copy this now. It won’t be shown again.</p>
              <div className="flex gap-2">
                <Input readOnly value={newToken.token} onFocus={(e) => e.target.select()} className="font-mono text-sm" />
                <CopyButton text={newToken.token} />
              </div>
              <details className="text-sm">
                <summary className="cursor-pointer text-clay">Config for Claude Desktop, Cursor and others</summary>
                <pre className="mt-2 overflow-x-auto rounded-md bg-mist p-3 text-xs">{JSON.stringify(newToken.config, null, 2)}</pre>
                <CopyButton text={JSON.stringify(newToken.config, null, 2)} label="Copy config" />
              </details>
            </div>
          )}

          <form onSubmit={createToken} className="flex gap-2">
            <Input
              placeholder="What it's for, e.g. Cursor on my laptop"
              value={tokenName}
              onChange={(e) => setTokenName(e.target.value)}
              maxLength={80}
              aria-label="Token name"
            />
            <Button type="submit" disabled={creating || !tokenName.trim()} className="shrink-0">
              {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Make token
            </Button>
          </form>

          {data.tokens.length > 0 && (
            <ul className="divide-y divide-border">
              {data.tokens.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{t.name}</p>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-mono">{t.prefix}…</span> · {t.days_left} days left
                      {t.last_used_at ? ` · last used ${date(t.last_used_at)}` : ''}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => revoke(t)}>Revoke</Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
